import React, {useEffect, useRef, useState} from "react";
import axios from "axios";
import * as Constants from "../../utils/Constants";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faFileExcel, faFilePdf, faGaugeHigh, faLocationDot, faRoute, faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ReportAssetFilter from "../ReportAssetFilter";
import {getAuthToken, getClientIdOfUser, showToast} from "../../utils/utility";
import {extractList} from "../../utils/responsePayload";
import {consolidateRouteSessions, consolidateTrips} from "../../utils/tripConsolidation";
import {findRouteForTrip, formatAlertBreakdown, perAssetTripNumbers, totalAlerts} from "../../utils/tripsReport";
import "../../styles/components/reports.css";
import "../../styles/components/trip-route.css";

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Parse a timestamp that may arrive as an ISO-8601 string or as epoch seconds (number).
 * Normalises "+HHMM" timezone offsets (no colon) to "+HH:MM" so all JS engines accept them.
 */
const parseTs = (v) => {
  if (!v) return null;
  if (typeof v === "number") return new Date(v * 1000);
  const normalized = String(v).replace(/([+-])(\d{2})(\d{2})$/, "$1$2:$3");
  return new Date(normalized);
};
const fmtTs = (v) => {
  const d = parseTs(v);
  return d && !isNaN(d) ? d.toLocaleString() : "—";
};
const minutes = (m) => (m == null ? "—" : `${Math.round(m)} min`);

const geoDistSq = (lat1, lng1, lat2, lng2) => {
  const dLat = lat2 - lat1, dLng = lng2 - lng1;
  return dLat * dLat + dLng * dLng;
};

const SEVERITY_COLOR = {
  REALTIME: "#ea580c",
  CRITICAL: "#dc2626",
  WARNING: "#f59e0b",
  OVERSPEED: "#f59e0b",
  INFO: "#0ea5e9",
};

/**
 * GPS Tracker "Trips" report — the former Trip Route and Trip Summary reports merged into one.
 * Both listed the same trips (same backend trip split, same stats); this screen shows the list
 * once, with the full per-trip summary, and draws the selected trip's route on the map.
 *
 * - List: `trip-summary` (one or many trackers; no selection = all trackers), so the fleet-wide
 *   trip table + Excel export that Trip Summary offered is kept.
 * - Map: the selected trip's polyline comes from `trip-route`, fetched lazily once per tracker per
 *   search and matched to the row by `sessionId` (`findRouteForTrip`).
 */
const Gt06Trips = () => {
  const [assetList, setAssetList] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});
  const TODAY = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(TODAY);
  const [endDate, setEndDate] = useState(TODAY);
  const [trips, setTrips] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRouteLoading, setIsRouteLoading] = useState(false);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const overlaysRef = useRef([]); // polyline + markers we add/remove per trip
  const hoverIWRef = useRef(null);
  // assetUnitId -> Promise<route sessions>, reset on every search (routes depend on the window).
  const routeCacheRef = useRef(new Map());
  const searchParamsRef = useRef(null);
  // Guards against a slow route fetch drawing over a trip the user has since moved away from.
  const renderTokenRef = useRef(0);

  useEffect(() => {
    (async () => {
      try {
        axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
        const res = await axios.get(
          `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${Constants.EP_ASSETS_GET_ALL}`
        );
        const all = res.data?.responseData || [];
        setAssetList(all.filter((a) => (a.assetType || "").toUpperCase() === "GT06_TRACKER"));
      } catch (err) {
        console.error("Error fetching GT06 assets:", err);
      }
    })();
  }, []);

  /** Create the map on first use — Google Maps may finish loading after this report mounts. */
  const ensureMap = () => {
    if (mapInstanceRef.current) return mapInstanceRef.current;
    if (!window.google || !window.google.maps || !mapRef.current) return null;
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      zoom: 12,
      center: {lat: 12.97, lng: 77.59},
      disableDefaultUI: false,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
    });
    return mapInstanceRef.current;
  };

  useEffect(() => {
    ensureMap();
  }, []);

  const assetNameById = (id) => assetList.find((a) => a.assetUnitId === id)?.name || id;

  const clearOverlays = () => {
    if (hoverIWRef.current) hoverIWRef.current.close();
    overlaysRef.current.forEach((o) => o.setMap(null));
    overlaysRef.current = [];
  };

  const loadRoutes = (assetUnitId) => {
    const cache = routeCacheRef.current;
    if (!cache.has(assetUnitId)) {
      const {startDate: s, endDate: e} = searchParamsRef.current || {};
      const params = new URLSearchParams({assetUnitId, startDate: s, endDate: e, timezone: TZ});
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const p = axios
        .get(`${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/gt06-reports/trip-route?${params}`)
        .then((res) => consolidateRouteSessions(res.data || []))
        .catch((err) => {
          cache.delete(assetUnitId); // allow a retry on the next click
          throw err;
        });
      cache.set(assetUnitId, p);
    }
    return cache.get(assetUnitId);
  };

  const drawRoute = (route) => {
    const map = ensureMap();
    if (!map || !route) return;
    clearOverlays();

    const pathWithTs = (route.points || []).filter((p) => p.lat != null && p.lng != null);
    const path = pathWithTs.map((p) => ({lat: p.lat, lng: p.lng}));
    if (path.length === 0) {
      showToast("No GPS-fix points in this trip.", {type: "info"});
      return;
    }

    // Route polyline — brand orange with a dark blue outline via two layered lines.
    const stroke = new window.google.maps.Polyline({
      path, geodesic: true, strokeColor: "#363166", strokeOpacity: 1.0, strokeWeight: 6, map,
    });
    const fill = new window.google.maps.Polyline({
      path, geodesic: true, strokeColor: "#FF683A", strokeOpacity: 1.0, strokeWeight: 3, map,
    });
    overlaysRef.current.push(stroke, fill);

    // Hover tooltip — nearest GPS point time & speed
    const hoverIW = new window.google.maps.InfoWindow({disableAutoPan: true});
    hoverIWRef.current = hoverIW;
    const showHover = (latLng) => {
      let nearest = pathWithTs[0], nearestD = Infinity;
      for (const p of pathWithTs) {
        const d = geoDistSq(latLng.lat(), latLng.lng(), p.lat, p.lng);
        if (d < nearestD) { nearestD = d; nearest = p; }
      }
      hoverIW.setPosition(latLng);
      hoverIW.setContent(
        `<div style="font-size:12px;line-height:1.5;padding:2px 4px">` +
        `<div style="font-weight:600">${fmtTs(nearest.t)}</div>` +
        `<div>Speed: ${nearest.speed ?? "—"} km/h</div>` +
        `</div>`
      );
      hoverIW.open(map);
    };
    [fill, stroke].forEach((line) => {
      line.addListener("mousemove", (e) => { if (e.latLng) showHover(e.latLng); });
      line.addListener("mouseout", () => hoverIW.close());
    });

    const endpoint = (position, text, fillColor) => new window.google.maps.Marker({
      map,
      position,
      label: {text, color: "#fff", fontWeight: "bold"},
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 14,
        fillColor,
        fillOpacity: 1,
        strokeColor: "#fff",
        strokeWeight: 2,
      },
    });
    overlaysRef.current.push(endpoint(path[0], "A", "#16a34a"), endpoint(path[path.length - 1], "B", "#dc2626"));

    // Alert markers
    const infoWindow = new window.google.maps.InfoWindow();
    (route.alertMarkers || []).forEach((alert) => {
      if (alert.lat == null || alert.lng == null) return;
      const m = new window.google.maps.Marker({
        map,
        position: {lat: alert.lat, lng: alert.lng},
        icon: {
          path: "M 0,-12 L 10,8 L -10,8 Z", // upward triangle
          fillColor: SEVERITY_COLOR[alert.severity] || "#0ea5e9",
          fillOpacity: 1,
          strokeColor: "#fff",
          strokeWeight: 1.5,
          scale: 1,
        },
        zIndex: 999,
      });
      m.addListener("click", () => {
        infoWindow.setContent(
          `<div style="font-size:13px"><strong>${alert.alertType}</strong><br/>` +
            `${fmtTs(alert.t)}<br/>Speed: ${alert.speed ?? "—"} km/h</div>`
        );
        infoWindow.open(map, m);
      });
      overlaysRef.current.push(m);
    });

    const bounds = new window.google.maps.LatLngBounds();
    path.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds);
  };

  const openTrip = async (trip) => {
    setActiveSessionId(trip.sessionId);
    const token = ++renderTokenRef.current;
    clearOverlays();
    setIsRouteLoading(true);
    try {
      const routes = await loadRoutes(trip.assetUnitId);
      if (token !== renderTokenRef.current) return;
      const route = findRouteForTrip(routes, trip);
      if (route) drawRoute(route);
      else showToast("Route not available for this trip.", {type: "info"});
    } catch (err) {
      if (token !== renderTokenRef.current) return;
      console.error("Trip route failed:", err);
      showToast(err?.response?.data?.responseMessage || "Failed to load the trip route", {type: "error"});
    } finally {
      if (token === renderTokenRef.current) setIsRouteLoading(false);
    }
  };

  const onSearch = async () => {
    const ids = Object.keys(checkedItems).filter((k) => checkedItems[k]);
    if (!startDate || !endDate) {
      showToast("Please select a date range.", {type: "error"});
      return;
    }
    setIsLoading(true);
    renderTokenRef.current++;
    clearOverlays();
    routeCacheRef.current = new Map();
    searchParamsRef.current = {startDate, endDate};
    try {
      const params = new URLSearchParams({startDate, endDate, timezone: TZ});
      if (ids.length) params.set("assetIds", ids.join(","));
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const res = await axios.get(
        `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/gt06-reports/trip-summary?${params}`
      );
      const list = extractList(res.data, ["responseData", "content", "data"], "trip-summary response");
      // Defensive merge of micro-trips (stop-and-go traffic). The backend already consolidates
      // with the same 30-min rule; against older API versions this keeps the list clean.
      const rows = consolidateTrips(list).map((r) => ({...r, assetName: assetNameById(r.assetUnitId)}));
      setTrips(rows);
      if (rows.length > 0) {
        openTrip(rows[0]);
      } else {
        setActiveSessionId(null);
        showToast("No trips in that window.", {type: "info"});
      }
    } catch (err) {
      console.error("Trips report failed:", err);
      showToast(err?.response?.data?.responseMessage || "Failed to load trips", {type: "error"});
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    const today = new Date().toISOString().split("T")[0];
    setStartDate(today);
    setEndDate(today);
    setCheckedItems({});
    setTrips([]);
    setActiveSessionId(null);
    renderTokenRef.current++;
    routeCacheRef.current = new Map();
    clearOverlays();
  };

  const tripNumbers = perAssetTripNumbers(trips);
  const multiAsset = new Set(trips.map((t) => t.assetUnitId)).size > 1;
  const activeIdx = trips.findIndex((t) => t.sessionId === activeSessionId);
  const active = activeIdx >= 0 ? trips[activeIdx] : null;
  const activeBreakdown = active ? formatAlertBreakdown(active.alerts) : "";

  const exportExcel = () => {
    if (!trips.length) return;
    const ws = XLSX.utils.json_to_sheet(
      trips.map((r, i) => ({
        Asset: r.assetName,
        IMEI: r.assetUnitId,
        Trip: tripNumbers[i],
        Start: fmtTs(r.startTime),
        End: fmtTs(r.endTime),
        DurationMin: r.durationMinutes,
        DistanceKm: r.distanceKm,
        MaxKmph: r.maxSpeedKmph,
        AvgKmph: r.avgSpeedKmph,
        IdleMin: r.idleMinutes,
        StopMin: r.stopMinutes,
        Points: r.pointCount,
        Alerts: formatAlertBreakdown(r.alerts),
      }))
    );
    const wb = {Sheets: {data: ws}, SheetNames: ["Trips"]};
    const buf = XLSX.write(wb, {bookType: "xlsx", type: "array"});
    FileSaver.saveAs(new Blob([buf]), `gt06_trips_${Date.now()}.xlsx`);
  };

  const exportPdf = () => {
    if (!trips.length) return;
    const doc = new jsPDF("l", "pt", "a4");
    doc.text(`GPS Tracker Trips — ${startDate} to ${endDate}`, 30, 30);
    autoTable(doc, {
      startY: 50,
      head: [["Asset", "IMEI", "#", "Start", "End", "Dur(min)", "Km", "Max km/h", "Avg km/h", "Idle(min)", "Alerts"]],
      body: trips.map((r, i) => [
        r.assetName, r.assetUnitId, tripNumbers[i], fmtTs(r.startTime), fmtTs(r.endTime),
        r.durationMinutes, r.distanceKm, r.maxSpeedKmph, r.avgSpeedKmph,
        r.idleMinutes != null ? Math.round(r.idleMinutes) : "", formatAlertBreakdown(r.alerts),
      ]),
      styles: {fontSize: 8},
    });
    doc.save(`gt06_trips_${Date.now()}.pdf`);
  };

  return (
    <div className="report-content-split">
      <div className="report-main-area">
        <div className="report-header-actions">
          <span className="report-results-title">
            Trips {trips.length > 0 && `· ${trips.length}`}
          </span>
          {trips.length > 0 && (
            <div className="export-buttons">
              <button className="export-btn" onClick={exportExcel}>
                <FontAwesomeIcon icon={faFileExcel} /> Excel
              </button>
              <button className="export-btn" onClick={exportPdf}>
                <FontAwesomeIcon icon={faFilePdf} /> PDF
              </button>
            </div>
          )}
        </div>

        {active && (
          <div className="trip-route-summary-strip">
            {multiAsset && (
              <div className="trsi trsi--datetime"><span className="trsi-label">Asset</span><span>{active.assetName}</span></div>
            )}
            <div className="trsi trsi--datetime"><span className="trsi-label">Start</span><span>{fmtTs(active.startTime)}</span></div>
            <div className="trsi trsi--datetime"><span className="trsi-label">End</span><span>{fmtTs(active.endTime)}</span></div>
            <div className="trsi trsi--stat"><span className="trsi-label">Duration</span><span>{minutes(active.durationMinutes)}</span></div>
            <div className="trsi trsi--stat"><span className="trsi-label">Distance</span><span>{active.distanceKm} km</span></div>
            <div className="trsi trsi--stat"><span className="trsi-label">Max</span><span>{active.maxSpeedKmph ?? "—"} km/h</span></div>
            <div className="trsi trsi--stat"><span className="trsi-label">Avg</span><span>{active.avgSpeedKmph ?? "—"} km/h</span></div>
            <div className="trsi trsi--stat"><span className="trsi-label">Idle</span><span>{minutes(active.idleMinutes)}</span></div>
            <div className="trsi trsi--stat"><span className="trsi-label">GPS Points</span><span>{active.pointCount ?? "—"}</span></div>
            <div className="trsi trsi--stat" title={activeBreakdown || undefined}>
              <span className="trsi-label">Alerts</span>
              <span>{activeBreakdown || "None"}</span>
            </div>
          </div>
        )}

        <div className="trip-route-map-and-list">
          <div className="trip-route-session-list">
            {isLoading && <div className="trsl-loading">Loading…</div>}
            {!isLoading && trips.length === 0 && (
              <div className="trsl-empty">
                <FontAwesomeIcon icon={faRoute} />
                <span>No trips yet. Pick GPS trackers (or none for all) and a date range, then Search.</span>
              </div>
            )}
            {!isLoading && trips.map((t, i) => {
              const alertCount = totalAlerts(t.alerts);
              return (
                <div
                  key={t.sessionId || `${t.assetUnitId}-${t.startTime}`}
                  className={`trsl-item ${t.sessionId === activeSessionId ? "active" : ""}`}
                  onClick={() => openTrip(t)}
                >
                  <div className="trsl-row1">
                    <span className="trsl-trip-num">
                      Trip {tripNumbers[i]}{multiAsset && ` · ${t.assetName}`}
                    </span>
                    <span className="trsl-distance">{t.distanceKm} km</span>
                  </div>
                  <div className="trsl-dates">
                    <div className="trsl-date-row">
                      <span className="trsl-date-label">Start</span>
                      <span className="trsl-date-val">{fmtTs(t.startTime)}</span>
                    </div>
                    <div className="trsl-date-row">
                      <span className="trsl-date-label">End</span>
                      <span className="trsl-date-val">{fmtTs(t.endTime)}</span>
                    </div>
                  </div>
                  <div className="trsl-row3">
                    <div className="trsl-stats">
                      <div className="trsl-stat">
                        <span className="trsl-stat-val">{t.durationMinutes}</span>
                        <span className="trsl-stat-label">min</span>
                      </div>
                      <div className="trsl-stat">
                        <span className="trsl-stat-val">{t.maxSpeedKmph}</span>
                        <span className="trsl-stat-label">max km/h</span>
                      </div>
                      <div className="trsl-stat">
                        <span className="trsl-stat-val">{t.avgSpeedKmph ?? "—"}</span>
                        <span className="trsl-stat-label">avg km/h</span>
                      </div>
                      <div className="trsl-stat">
                        <span className="trsl-stat-val">{t.idleMinutes != null ? Math.round(t.idleMinutes) : "—"}</span>
                        <span className="trsl-stat-label">idle min</span>
                      </div>
                    </div>
                    {alertCount > 0 && (
                      <span className="trsl-alert-badge" title={formatAlertBreakdown(t.alerts)}>
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                        {alertCount}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="trip-route-map-wrap">
            <div ref={mapRef} className="trip-route-map" />
            {isRouteLoading && <div className="trip-route-map-loading">Loading route…</div>}
          </div>
        </div>
      </div>

      <div className="report-filter-sidebar">
        <div className="sidebar-header">
          <h3 className="sidebar-title">Search Filters</h3>
        </div>
        <div className="sidebar-content">
          <div className="filter-group">
            <label className="filter-label">Select GPS Trackers</label>
            <div className="asset-list-wrapper">
              <ReportAssetFilter assets={assetList} checkedItems={checkedItems} setCheckedItems={setCheckedItems} />
            </div>
          </div>
          <div>
            <label className="filter-label">Start Date</label>
            <input
              type="date"
              className="filter-input"
              value={startDate}
              max={TODAY}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="filter-label">End Date</label>
            <input
              type="date"
              className="filter-input"
              value={endDate}
              max={TODAY}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="trip-route-legend">
            <div className="trl-title">Legend</div>
            <div className="trl-row"><span className="trl-dot" style={{background: "#16a34a"}}/><span>Start (A)</span></div>
            <div className="trl-row"><span className="trl-dot" style={{background: "#dc2626"}}/><span>End (B)</span></div>
            <div className="trl-row"><span className="trl-line"/><span>Route</span></div>
            <div className="trl-row">
              <FontAwesomeIcon icon={faLocationDot} style={{color: "#dc2626"}}/><span>Critical alert</span>
            </div>
            <div className="trl-row">
              <FontAwesomeIcon icon={faLocationDot} style={{color: "#f59e0b"}}/><span>Warning alert</span>
            </div>
            <div className="trl-row">
              <FontAwesomeIcon icon={faGaugeHigh} style={{color: "#f59e0b"}}/><span>Overspeed</span>
            </div>
          </div>
        </div>
        <div className="sidebar-footer">
          <div className="filter-actions">
            <button className="btn-report" onClick={onSearch}>Search</button>
            <button className="btn-report btn-secondary" onClick={handleClear}>Clear</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Gt06Trips;
